import { useEffect, useMemo, useState } from "react";
import type { EditingProject } from "../types/project";
import {
  detailDataCoversViewport,
  planDetailRange,
  type DetailWaveformData,
} from "./detail";
import { isWaveformRequestSuperseded, type WaveformService } from "./service";
import type { WaveformPyramid } from "./types";

type Options = {
  file: File;
  project: EditingProject;
  service: WaveformService;
  centerFrame: number;
  visibleFrames: number;
  overview: WaveformPyramid | null;
  debounceMs: number;
  onTiming: (seconds: number) => void;
  enabled?: boolean;
};

export type DetailWaveformView = {
  pyramid: WaveformPyramid | null;
  dataStartFrame: number;
  source: "detail" | "overview" | "empty";
  loading: boolean;
};

export function useDetailWaveform({
  file,
  project,
  service,
  centerFrame,
  visibleFrames,
  overview,
  debounceMs,
  onTiming,
  enabled = true,
}: Options): DetailWaveformView {
  const sourceKey = `${project.id}:${file.size}:${file.lastModified}`;
  const [storedData, setStoredData] = useState<
    (DetailWaveformData & { sourceKey: string }) | null
  >(null);
  const [loadingKey, setLoadingKey] = useState<string | null>(null);
  const [retryState, setRetryState] = useState({ key: "", count: 0 });
  const data = storedData?.sourceKey === sourceKey ? storedData : null;
  const range = useMemo(
    () =>
      planDetailRange(
        centerFrame,
        visibleFrames,
        project.durationFrames,
        project.sampleRate,
      ),
    [centerFrame, visibleFrames, project.durationFrames, project.sampleRate],
  );
  const covered = detailDataCoversViewport(
    data,
    centerFrame,
    visibleFrames,
    project.durationFrames,
  );
  const rangeStart = range?.startFrame ?? -1;
  const rangeEnd = range?.endFrame ?? -1;
  const rangeKey = `${sourceKey}:${rangeStart}:${rangeEnd}`;
  const retry = retryState.key === rangeKey ? retryState.count : 0;
  const loading =
    enabled && !covered && loadingKey === rangeKey && rangeStart >= 0;

  useEffect(() => {
    if (!enabled || rangeStart < 0 || rangeEnd <= rangeStart || covered) return;

    let active = true;
    let retryTimer: number | null = null;
    const timer = window.setTimeout(() => {
      setLoadingKey(rangeKey);
      const bucketCount = Math.min(
        8192,
        Math.max(
          1800,
          Math.ceil((1800 * (rangeEnd - rangeStart)) / visibleFrames),
        ),
      );
      void service
        .analyzeDetail(file, project, rangeStart, rangeEnd, bucketCount)
        .then((result) => {
          if (!active) return;
          setStoredData({
            pyramid: result.pyramid,
            startFrame: rangeStart,
            endFrame: rangeEnd,
            sourceKey,
          });
          setLoadingKey(null);
          onTiming(result.elapsedMs / 1000);
        })
        .catch((error: unknown) => {
          if (!active) return;
          setLoadingKey(null);
          if (isWaveformRequestSuperseded(error)) return;
          if (retry < 2) {
            retryTimer = window.setTimeout(
              () =>
                setRetryState((value) => ({
                  key: rangeKey,
                  count: value.key === rangeKey ? value.count + 1 : 1,
                })),
              500 * (retry + 1),
            );
          }
        });
    }, debounceMs);

    return () => {
      active = false;
      window.clearTimeout(timer);
      if (retryTimer !== null) window.clearTimeout(retryTimer);
    };
  }, [
    covered,
    debounceMs,
    enabled,
    file,
    onTiming,
    project,
    rangeEnd,
    rangeKey,
    rangeStart,
    retry,
    service,
    sourceKey,
    visibleFrames,
  ]);

  if (covered && data) {
    return {
      pyramid: data.pyramid,
      dataStartFrame: data.startFrame,
      source: "detail",
      loading,
    };
  }
  if (overview) {
    return {
      pyramid: overview,
      dataStartFrame: 0,
      source: "overview",
      loading,
    };
  }
  return { pyramid: null, dataStartFrame: 0, source: "empty", loading };
}
